"use client";
import { FC, ReactNode, useEffect, useState } from "react";
import { IBanks } from "@/interfaces";
import { FlexBetween, FlexBetweenCol } from "./ui/flexBox";
import {
  cn,
  findBankByCardNumber,
  findBankByIban,
  formatCardNumberGroups,
  formatIbanGroups,
  hexToRgba,
} from "@/lib";
import { BoBuildings2 } from "solar-icon-react/bo";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useGetBanks } from "@/hooks";
import cardBg from "public/creditCardBg.png";
import { useToastStore } from "@/store/toastStore";
import { OlCopy } from "solar-icon-react/ol";

interface IProps {
  variant: "credit" | "iban" | "account";
  accountNumber?: string;
  cardNumber?: string;
  cardStatus?: string;
  owners?: string;
  cardValues: {
    label: string;
    value: string;
  };
  headerRight?: (ctx: CreditCardCtx) => ReactNode;
  footer?: (ctx: CreditCardCtx) => ReactNode;
}

type CreditCardCtx = {
  bankInfo: IBanks | null;
  isIban: boolean;
  color?: string;
  cardValues: { label: string; value: string };
  owners?: string;
  accountNumber?: string;
  cardNumber?: string;
  cardStatus?: string;
};

const DetailCreditCard: FC<IProps> = ({
  variant,
  accountNumber,
  cardNumber,
  cardStatus,
  owners,
  cardValues,
  headerRight,
  footer,
}) => {
  const t = useTranslations();
  const { data: bankOptions } = useGetBanks();
  const [bankInfo, setBankInfo] = useState<IBanks | null>(null);
  const isIban = variant === "iban";

  useEffect(() => {
    const findBank = isIban ? findBankByIban : findBankByCardNumber;
    const valueToFind = variant === "account" ? cardNumber : cardValues.value;
    if (!valueToFind) return;

    const result = findBank(valueToFind, bankOptions?.data);

    setBankInfo(result || null);
  }, [cardValues.value, isIban, cardNumber]);

  const { setOpenToast } = useToastStore();
  const copyToClipboard = () => {
    navigator.clipboard.writeText(cardValues.value);
    setOpenToast({
      variant: "success",
      message: "کپی شد",
    });
  };

  const ctx: CreditCardCtx = {
    bankInfo,
    isIban,
    color: bankInfo?.color,
    cardValues,
    owners,
    accountNumber,
    cardNumber,
    cardStatus,
  };

  return (
    <FlexBetweenCol
      className={cn(
        "relative w-full min-w-[300px] rounded-xl bg-white py-4 lg:py-6",
      )}
      style={
        bankInfo
          ? {
              background: `
              radial-gradient(
              circle at 10% 30%,
              ${hexToRgba(bankInfo.color, 0.4)} 0%,
              ${hexToRgba(bankInfo.color, 0.06)} 40%,
              ${hexToRgba(bankInfo.color, 0.12)} 70%,
              ${hexToRgba(bankInfo.color, 0.3)} 100%
            )`,
            }
          : undefined
      }
    >
      <div className="absolute top-0 right-0 bottom-0 left-0 z-10 h-full w-full">
        <Image
          src={cardBg}
          alt={bankInfo?.label || ""}
          className="h-full w-full rounded-lg object-cover"
        />
      </div>
      <header
        className={cn(
          "mb-3 flex h-7 w-full items-center justify-between overflow-hidden px-4 lg:mb-4 lg:h-10 lg:px-8",
          !bankInfo && "bg-gray-200",
        )}
      >
        <FlexBetween gap={2}>
          {bankInfo ? (
            <div style={{ width: "32px", height: "32px" }}>
              <img
                src={bankInfo.logo}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                alt={bankInfo.label}
              />
            </div>
          ) : (
            <BoBuildings2 className="text-primary-main!" />
          )}
          <span
            style={{ color: bankInfo?.color }}
            className={cn("text-sm lg:text-base")}
          >
            {bankInfo ? bankInfo.label : t("creditCard.bankName")}
          </span>
        </FlexBetween>
        {headerRight ? <div>{headerRight(ctx)}</div> : ""}
      </header>

      <FlexBetween
        gap={2}
        className={cn("relative z-50 w-full flex-col px-4 lg:px-8 xl:gap-3")}
      >
        <div
          onClick={() => copyToClipboard()}
          className={cn(
            `${variant === "account" || variant === "credit" ? "mx-auto lg:w-[80%]" : "w-full"} flex cursor-pointer flex-col items-end`,
          )}
        >
          <div
            style={{ color: bankInfo?.color }}
            className="text-micro flex items-center gap-1 font-light"
          >
            <OlCopy size={14} />
            {cardValues.label}
          </div>
          <p
            style={{
              color: bankInfo?.color,
              wordSpacing: isIban ? "4px" : "14px",
            }}
            className="dir-ltr text-base font-bold lg:text-[22px]"
          >
            {variant === "account"
              ? cardValues.value
              : isIban
                ? formatIbanGroups(cardValues.value)
                : formatCardNumberGroups(cardValues.value)}
          </p>
        </div>
        {owners && (
          <p className="text-center text-base font-semibold lg:text-lg">
            {owners}
          </p>
        )}
        {footer ? <div className="w-full px-4">{footer(ctx)}</div> : ""}
      </FlexBetween>
    </FlexBetweenCol>
  );
};

export default DetailCreditCard;
